"use client"

import type React from "react"

import { createContext, useContext, useEffect, useState } from "react"
import { createTheme, ThemeProvider as MUIThemeProvider } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"

type Theme = "light" | "dark"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "light",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "stone-chat-theme", // Use the storage key from the example
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Load initial theme from localStorage or use default
    try {
        const savedTheme = localStorage.getItem(storageKey) as Theme | null;
        if (savedTheme && (savedTheme === "light" || savedTheme === "dark")) {
          return savedTheme;
        }
    } catch (error) {
        console.error("Error reading theme from localStorage", error);
    }
    return defaultTheme;
  });

  useEffect(() => {
    try {
        localStorage.setItem(storageKey, theme)
    } catch (error) {
        console.error("Error saving theme to localStorage", error);
    }
  }, [storageKey, theme])

  // StoneChat Light Theme Configuration (Purple)
  const lightTheme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#9333ea", // Purple
        light: "#f3e8ff", // Light Purple
        dark: "#7e22ce", // Darker Purple
        contrastText: "#ffffff",
      },
      secondary: {
        main: "#f3e8ff", // Light Purple
        light: "#faf5ff", // Lighter Purple
        dark: "#d8b4fe",
        contrastText: "#1f2937",
      },
      background: {
        default: "#ffffff", // White
        paper: "#f9fafb", // Very Light Gray
      },
      text: {
        primary: "#1f2937", // Dark Gray near Black
        secondary: "#6b7280", // Medium Gray
      },
      divider: "#e5e7eb", // Light Gray
      error: { // Ensure error color is defined
        main: '#f44336', // Default MUI red
      },
    },
    typography: {
      fontFamily: '"Inter", "Noto Sans SC", "Roboto", "Helvetica", "Arial", sans-serif',
      button: { // Ensure button text transform matches stone-chat
         textTransform: 'none',
      }
    },
    shape: { // Ensure border radius matches stone-chat
        borderRadius: 12, // Example used rounded corners extensively
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a8a8a8",
            },
          },
        },
      },
       MuiPaper: { // Ensure paper matches stone-chat style
         defaultProps: {
           elevation: 0,
         },
         styleOverrides: {
           root: {
             transition: "box-shadow 0.3s ease-in-out",
             // Add border radius based on theme
             borderRadius: 12, // Match theme shape
           },
         },
       },
       MuiButton: { // Ensure button matches stone-chat style
           defaultProps: {
              disableElevation: true, // No shadow by default
           },
           styleOverrides: {
              root: {
                 borderRadius: 8, // Consistent button radius
                 textTransform: 'none',
              }
           }
       },
       MuiTextField: { // Add potential styling for TextField to match stone-chat
          styleOverrides: {
              root: {
                 '& .MuiOutlinedInput-root': {
                     borderRadius: 12, // Match paper/theme shape
                      // Add transition for focus/hover effects seen in example
                      transition: 'box-shadow 0.3s ease',
                      backgroundColor: '#ffffff', // Default light background
                     '&.Mui-focused': {
                        boxShadow: '0 4px 12px rgba(147, 51, 234, 0.15)', // Purple focus shadow
                      },
                      '&:hover:not(.Mui-disabled)': {
                           boxShadow: '0 2px 8px rgba(147, 51, 234, 0.1)', // Purple hover shadow
                      }
                 },
              }
          }
       }
      // Add other component overrides from StoneChat if needed
    },
  })

  // StoneChat Dark Theme Configuration (Purple)
  const darkTheme = createTheme({
    palette: {
      mode: "dark",
      primary: {
        main: "#a855f7", // Adjusted Purple for dark mode contrast
        light: "rgba(168, 85, 247, 0.2)", // Use adjusted purple alpha
        dark: "#9333ea", // Base purple as dark variant
        contrastText: "#ffffff",
      },
      secondary: {
        main: "rgba(168, 85, 247, 0.15)", // Adjusted purple alpha
        light: "#d8b4fe", // Keep light purple for accents
        dark: "#7e22ce", // Darker purple
        contrastText: "#ffffff",
      },
      background: {
        default: "#111827", // Very Dark Gray
        paper: "#1f2937", // Dark Gray
      },
      text: {
        primary: "#ffffff", // White
        secondary: "#9ca3af", // Light Gray
      },
      divider: "#374151", // Medium Dark Gray
      error: {
        main: "#fca5a5", // Lighter Red for dark mode
      },
    },
     typography: {
      fontFamily: '"Inter", "Noto Sans SC", "Roboto", "Helvetica", "Arial", sans-serif',
       button: {
         textTransform: 'none',
      }
    },
     shape: {
        borderRadius: 12, // Match light theme
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#2d2d2d",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#555555",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#666666",
            },
          },
        },
      },
        MuiPaper: {
         defaultProps: {
           elevation: 0,
         },
         styleOverrides: {
           root: {
             transition: "box-shadow 0.3s ease-in-out",
             borderRadius: 12, // Match theme shape
              // Add dark mode specific hover/focus if needed
              "&:hover": {
                 boxShadow: "0 4px 12px rgba(168, 85, 247, 0.15)", // Example used purple shadow
              },
           },
         },
       },
        MuiButton: {
           defaultProps: {
              disableElevation: true,
           },
           styleOverrides: {
              root: {
                 borderRadius: 8,
                 textTransform: 'none',
              }
           }
       },
        MuiTextField: {
          styleOverrides: {
              root: {
                 '& .MuiOutlinedInput-root': {
                     borderRadius: 12,
                      transition: 'box-shadow 0.3s ease',
                      backgroundColor: '#1f2937', // Default dark paper background
                     '&.Mui-focused': {
                        boxShadow: '0 4px 12px rgba(168, 85, 247, 0.2)', // Darker purple focus shadow
                      },
                     '&:hover:not(.Mui-disabled)': {
                           boxShadow: '0 2px 8px rgba(168, 85, 247, 0.15)', // Darker purple hover shadow
                      }
                 },
                 '& .MuiInputBase-input': {
                     // Ensure text color contrasts with dark background
                     color: '#ffffff',
                 },
                 '& .MuiOutlinedInput-notchedOutline': {
                     borderColor: '#374151', // Match divider color
                 },
                 '&:hover .MuiOutlinedInput-notchedOutline': {
                     borderColor: '#9ca3af', // Match text secondary
                 },
                 '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#a855f7', // Match primary main
                 },
              }
          }
       }
    },
  })

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      setTheme(theme)
    },
  }

  return (
    <ThemeProviderContext.Provider value={value} {...props}>
      <MUIThemeProvider theme={theme === "light" ? lightTheme : darkTheme}>
        {/* CssBaseline applies base styling and background color */}
        <CssBaseline enableColorScheme />
        {children}
      </MUIThemeProvider>
    </ThemeProviderContext.Provider>
  )
}

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  return context
} 